import React, { useState, useCallback, useMemo } from 'react';

import { InboxOutlined, UploadOutlined, DownloadOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { Steps, Card, Upload, Button, Table, message, Space, Alert, Progress, Select } from 'antd';
import Papa from 'papaparse';
import { useTranslation } from 'react-i18next';

const { Dragger } = Upload;

// 配對狀態（"狀態" 欄位用）。兩條路徑都會落到這幾個值；新增任一狀態時，
// 必須同時更新：(1) 純 PDF 初始化 (parseData no-CSV branch)、(2) CSV 初始化、
// (3) fulfillment 驗證覆寫、(4) previewColumns 的 render dispatch。
//
// MATCHED 的語意在兩條路徑下略有不同 — CSV 路徑是「CSV row 的 filename 對到實際 PDF」，
// 純 PDF 路徑是「檔案本身即 row source」；兩者在後續流程視為同一狀態（皆代表「準備好上傳」）。
const ROW_STATUS = {
  MATCHED: 'matched',
  MANUALLY_MATCHED: 'manually_matched',
  MISSING: 'missing',
  FULFILLMENT_NOT_FOUND: 'fulfillment_not_found',
};

// 上傳結果（"上傳結果" 欄位用）
const UPLOAD_STATUS = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  SUCCESS: 'success',
  FAILED: 'failed',
};

/**
 * Shared 組件：上傳物流標籤（三階段 Wizard）
 *
 * 階段 1: 上傳 PDF 標籤檔案（可 0 個或多個）
 * 階段 2: 上傳 CSV 對應檔（可選，格式：出貨單名稱,TrackingNumber,檔名）
 * 階段 3: 預覽並確認上傳
 *
 * @param {Object} orderService - Order Service (包含 validateFulfillmentNames 和 uploadLabel)
 * @param {Object} fulfillmentService - Fulfillment Service (包含 fetchList，用於搜尋)
 * @param {Function} onSuccess - 上傳成功回調
 * @param {Function} onCancel - 取消回調
 */
export default function ShipmentLabelUpload({ orderService, fulfillmentService, onSuccess, onCancel }) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [pdfFiles, setPdfFiles] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fulfillment 搜尋相關 state
  const [fulfillmentSearchResults, setFulfillmentSearchResults] = useState({});
  const [fulfillmentSearchLoading, setFulfillmentSearchLoading] = useState({});

  // ========== Step 1: PDF 上傳 ==========
  const pdfUploadProps = {
    name: 'file',
    multiple: true,
    accept: '.pdf',
    fileList: pdfFiles,
    beforeUpload: (file) => {
      // 檢查檔案類型
      if (file.type !== 'application/pdf') {
        message.error(t('shared.orders.upload_labels.errors.invalid_pdf'));
        return Upload.LIST_IGNORE;
      }

      // 檢查檔名是否重複
      const isDuplicate = pdfFiles.some(f => f.name === file.name);
      if (isDuplicate) {
        message.warning(t('shared.orders.upload_labels.errors.duplicate_filename', { filename: file.name }));
        return Upload.LIST_IGNORE;
      }

      setPdfFiles(prev => [...prev, file]);
      return false; // 阻止自動上傳
    },
    onRemove: (file) => {
      setPdfFiles(prev => prev.filter(f => f.uid !== file.uid));
    },
  };

  // ========== Step 2: CSV 上傳 ==========
  const csvUploadProps = {
    name: 'file',
    accept: '.csv',
    fileList: csvFile ? [csvFile] : [],
    beforeUpload: (file) => {
      // 檢查檔案類型
      if (!file.name.endsWith('.csv')) {
        message.error(t('shared.orders.upload_labels.errors.invalid_csv'));
        return Upload.LIST_IGNORE;
      }

      setCsvFile(file);
      return false; // 阻止自動上傳
    },
    onRemove: () => {
      setCsvFile(null);
    },
    maxCount: 1,
  };

  // 下載 CSV 範例（使用 i18n header）
  const handleDownloadTemplate = useCallback(() => {
    const headers = [
      t('shared.orders.upload_labels.csv_template.fulfillment_name'),
      t('shared.orders.upload_labels.csv_template.tracking_number'),
      t('shared.orders.upload_labels.csv_template.filename'),
    ];

    const sampleData = [
      ['ORD-20250328-001', 'TN123456789', 'ORD-20250328-001.pdf'],
      ['ORD-20250328-002', 'TN987654321', 'ORD-20250328-002.pdf'],
    ];

    const csvRows = [
      headers.join(','),
      ...sampleData.map(row => row.join(','))
    ];
    const csvContent = csvRows.join('\n');

    const blob = new Blob([`\ufeff${  csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'shipment_labels_template.csv';
    link.click();
  }, [t]);

  // ========== Step 3: 解析 CSV 並預覽 ==========
  const handleParseCSV = useCallback(async () => {
    if (!csvFile && pdfFiles.length === 0) {
      message.warning(t('shared.orders.upload_labels.errors.no_files'));
      return;
    }

    setLoading(true);

    try {
      let data = [];

      // 如果沒有 CSV，直接從 PDF 檔名提取出貨單名稱
      if (!csvFile) {
        data = pdfFiles.map(file => {
          const fulfillmentName = file.name.replace(/\.(pdf|PDF)$/, '');
          return {
            fulfillmentName,
            trackingNumber: '',
            filename: file.name,
            file,
            status: ROW_STATUS.MATCHED,
          };
        });
      } else {
        // 解析 CSV
        await new Promise((resolve, reject) => {
          Papa.parse(csvFile, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              const i18nHeaders = {
                fulfillmentName: t('shared.orders.upload_labels.csv_template.fulfillment_name'),
                trackingNumber: t('shared.orders.upload_labels.csv_template.tracking_number'),
                filename: t('shared.orders.upload_labels.csv_template.filename'),
              };

              data = results.data.map(row => {
                // 支援 i18n header、英文 key、舊版中文 header（向後相容）
                const fulfillmentName = row[i18nHeaders.fulfillmentName] || row.fulfillment_name || row['出貨單名稱'] || row['訂單號碼'] || '';
                const trackingNumber = row[i18nHeaders.trackingNumber] || row.tracking_number || row.TrackingNumber || '';
                const filename = row[i18nHeaders.filename] || row.filename || row['檔名'] || '';

                const matchedFile = pdfFiles.find(f => f.name === filename);

                return {
                  fulfillmentName,
                  trackingNumber,
                  filename,
                  file: matchedFile,
                  status: matchedFile ? ROW_STATUS.MATCHED : ROW_STATUS.MISSING,
                };
              });

              resolve();
            },
            error: (error) => {
              reject(error);
            },
          });
        });
      }

      // 驗證出貨單是否存在
      const fulfillmentNames = data.map(item => item.fulfillmentName).filter(Boolean);
      const validateResult = await orderService.validateFulfillmentNames(fulfillmentNames);

      if (!validateResult.success) {
        message.error(validateResult.message);
        setLoading(false);
        return;
      }

      // 標記不存在的出貨單
      const missingFulfillmentNames = validateResult.data.missing_fulfillment_names || [];
      data = data.map(item => ({
        ...item,
        fulfillmentExists: !missingFulfillmentNames.includes(item.fulfillmentName),
        status: missingFulfillmentNames.includes(item.fulfillmentName) ? ROW_STATUS.FULFILLMENT_NOT_FOUND : item.status,
      }));

      setParsedData(data);
      setCurrentStep(2);
      setLoading(false);
    } catch (error) {
      console.error('[ShipmentLabelUpload] Parse CSV failed:', error);
      message.error(t('shared.orders.upload_labels.errors.csv_parse_failed', { error: error.message }));
      setLoading(false);
    }
  }, [csvFile, pdfFiles, t, orderService]);

  // ========== Fulfillment 搜尋（用於對不到時的下拉選擇）==========
  const handleSearchFulfillment = useCallback(async (rowIndex, searchText) => {
    if (!searchText || searchText.length < 2 || !fulfillmentService) return;

    setFulfillmentSearchLoading(prev => ({ ...prev, [rowIndex]: true }));

    try {
      const result = await fulfillmentService.fetchList({
        'fulfillments.name': [searchText],
        page_size: 20,
      });
      if (result.success) {
        setFulfillmentSearchResults(prev => ({
          ...prev,
          [rowIndex]: result.data.map(f => ({ value: f.name, label: `${f.name} (${f.number})` })),
        }));
      }
    } catch (error) {
      console.error('[ShipmentLabelUpload] Search fulfillment failed:', error);
    } finally {
      setFulfillmentSearchLoading(prev => ({ ...prev, [rowIndex]: false }));
    }
  }, [fulfillmentService]);

  // 選擇 Fulfillment
  const handleSelectFulfillment = useCallback((rowIndex, fulfillmentName) => {
    setParsedData(prev =>
      prev.map((p, idx) =>
        idx === rowIndex ? { ...p, fulfillmentName, fulfillmentExists: true, status: ROW_STATUS.MANUALLY_MATCHED } : p
      )
    );
  }, []);

  // ========== 逐筆上傳 ==========
  const handleUpload = useCallback(async () => {
    if (parsedData.length === 0) {
      message.warning(t('shared.orders.upload_labels.errors.no_data'));
      return;
    }

    // 過濾掉仍然對不到的行
    const uploadableData = parsedData.filter(
      item => item.fulfillmentExists && item.status !== ROW_STATUS.FULFILLMENT_NOT_FOUND
    );

    if (uploadableData.length === 0) {
      message.error(t('shared.orders.upload_labels.errors.invalid_data'));
      return;
    }

    setLoading(true);

    let successCount = 0;
    let failedCount = 0;
    const failedItems = [];

    // 初始化所有項目的上傳狀態
    const updatedData = parsedData.map(item => ({
      ...item,
      uploadStatus: item.fulfillmentExists ? UPLOAD_STATUS.PENDING : undefined,
      uploadError: null,
    }));
    setParsedData(updatedData);

    // 逐筆上傳
    for (let i = 0; i < parsedData.length; i++) {
      const item = parsedData[i];

      // 跳過對不到的行
      if (!item.fulfillmentExists || item.status === ROW_STATUS.FULFILLMENT_NOT_FOUND) continue;

      // 更新狀態為 uploading
      setParsedData(prev =>
        prev.map((p, idx) =>
          idx === i ? { ...p, uploadStatus: UPLOAD_STATUS.UPLOADING } : p
        )
      );

      try {
        const formData = new FormData();

        // 使用 fulfillment_name 作為 key
        formData.append('fulfillment_name', item.fulfillmentName);
        if (item.trackingNumber) {
          formData.append('tracking_number', item.trackingNumber);
        }
        if (item.file) {
          formData.append('label', item.file);
        }

        const uploadResult = await orderService.uploadLabel(formData);

        if (uploadResult.success) {
          // 上傳成功
          successCount++;
          setParsedData(prev =>
            prev.map((p, idx) =>
              idx === i ? { ...p, uploadStatus: UPLOAD_STATUS.SUCCESS } : p
            )
          );
        } else {
          // 上傳失敗
          failedCount++;
          failedItems.push({
            fulfillmentName: item.fulfillmentName,
            error: uploadResult.message,
          });
          setParsedData(prev =>
            prev.map((p, idx) =>
              idx === i ? {
                ...p,
                uploadStatus: UPLOAD_STATUS.FAILED,
                uploadError: uploadResult.message,
              } : p
            )
          );
        }
      } catch (error) {
        // 網路錯誤或其他異常
        console.error(`[ShipmentLabelUpload] Upload item ${i} failed:`, error);
        failedCount++;
        failedItems.push({
          fulfillmentName: item.fulfillmentName,
          error: error.message,
        });
        setParsedData(prev =>
          prev.map((p, idx) =>
            idx === i ? {
              ...p,
              uploadStatus: UPLOAD_STATUS.FAILED,
              uploadError: error.message,
            } : p
          )
        );
      }
    }

    setLoading(false);

    // 顯示結果
    if (failedCount > 0) {
      message.warning(
        t('shared.orders.upload_labels.success.partial', {
          success: successCount,
          failed: failedCount,
          failedList: failedItems.map(f => f.fulfillmentName).join(', '),
        })
      );
    } else {
      message.success(t('shared.orders.upload_labels.success.complete', { count: successCount }));
      // 全部成功才呼叫 onSuccess
      onSuccess && onSuccess({ total: uploadableData.length, success: successCount, failed: 0 });
    }
  }, [parsedData, t, onSuccess, orderService]);

  // ========== 預覽 Table Columns ==========
  const previewColumns = useMemo(() => [
    {
      title: t('shared.orders.upload_labels.preview.fulfillment_name'),
      dataIndex: 'fulfillmentName',
      key: 'fulfillmentName',
      render: (text, record, index) => {
        if (record.status === ROW_STATUS.FULFILLMENT_NOT_FOUND) {
          return (
            <Select
              showSearch
              placeholder={t('shared.orders.upload_labels.preview.search_fulfillment')}
              defaultValue={text}
              loading={fulfillmentSearchLoading[index]}
              options={fulfillmentSearchResults[index] || []}
              onSearch={(val) => handleSearchFulfillment(index, val)}
              onChange={(val) => handleSelectFulfillment(index, val)}
              style={{ width: '100%' }}
              filterOption={false}
              notFoundContent={fulfillmentSearchLoading[index] ? <LoadingOutlined /> : null}
            />
          );
        }
        return text;
      },
    },
    {
      title: t('shared.orders.upload_labels.preview.tracking_number'),
      dataIndex: 'trackingNumber',
      key: 'trackingNumber',
      render: (text) => text || '-',
    },
    {
      title: t('shared.orders.upload_labels.preview.filename'),
      dataIndex: 'filename',
      key: 'filename',
      render: (text) => text || '-',
    },
    {
      title: t('shared.orders.upload_labels.preview.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        if (status === ROW_STATUS.FULFILLMENT_NOT_FOUND) {
          return <span style={{ color: '#ff4d4f' }}>{t('shared.orders.upload_labels.preview.fulfillment_not_found')}</span>;
        } else if (status === ROW_STATUS.MATCHED || status === ROW_STATUS.MANUALLY_MATCHED) {
          return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
        } else if (status === ROW_STATUS.MISSING) {
          return <span style={{ color: '#ff4d4f' }}>{t('shared.orders.upload_labels.preview.file_missing')}</span>;
        }
          return <span style={{ color: '#faad14' }}>{t('shared.orders.upload_labels.preview.pending')}</span>;

      },
    },
    {
      title: t('shared.orders.upload_labels.preview.upload_status'),
      dataIndex: 'uploadStatus',
      key: 'uploadStatus',
      render: (uploadStatus, record) => {
        if (uploadStatus === UPLOAD_STATUS.SUCCESS) {
          return (
            <span style={{ color: '#52c41a' }}>
              <CheckCircleOutlined /> {t('shared.orders.upload_labels.preview.upload_success')}
            </span>
          );
        } else if (uploadStatus === UPLOAD_STATUS.FAILED) {
          return (
            <span style={{ color: '#ff4d4f' }}>
              <CloseCircleOutlined /> {t('shared.orders.upload_labels.preview.upload_failed')}
              {record.uploadError && <div style={{ fontSize: '12px' }}>({record.uploadError})</div>}
            </span>
          );
        } else if (uploadStatus === UPLOAD_STATUS.UPLOADING) {
          return (
            <span style={{ color: '#1890ff' }}>
              <LoadingOutlined /> {t('shared.orders.upload_labels.preview.uploading')}
            </span>
          );
        }
          return <span style={{ color: '#999' }}>-</span>;

      },
    },
  ], [t, fulfillmentSearchResults, fulfillmentSearchLoading, handleSearchFulfillment, handleSelectFulfillment]);

  // ========== Steps 配置 ==========
  const steps = [
    {
      title: t('shared.orders.upload_labels.steps.upload_pdf'),
      content: (
        <div style={{ padding: '24px 0' }}>
          <Dragger {...pdfUploadProps} style={{ padding: '20px' }}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{t('shared.orders.upload_labels.step1.drag_text')}</p>
            <p className="ant-upload-hint">{t('shared.orders.upload_labels.step1.hint')}</p>
          </Dragger>

          {pdfFiles.length > 0 && (
            <Alert
              message={t('shared.orders.upload_labels.step1.files_selected', { count: pdfFiles.length })}
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </div>
      ),
    },
    {
      title: t('shared.orders.upload_labels.steps.upload_csv'),
      content: (
        <div style={{ padding: '24px 0' }}>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* CSV 格式範例標題 + 下載按鈕 */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <span style={{ fontWeight: 500, fontSize: '14px' }}>
                  {t('shared.orders.upload_labels.step2.format_example_title')}
                </span>
                <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
                  {t('shared.orders.upload_labels.step2.download_template')}
                </Button>
              </div>

              {/* CSV 格式示範表格 */}
              <Table
                size="small"
                pagination={false}
                bordered
                columns={[
                  {
                    title: t('shared.orders.upload_labels.csv_template.fulfillment_name'),
                    dataIndex: 'fulfillmentName',
                    key: 'fulfillmentName',
                  },
                  {
                    title: t('shared.orders.upload_labels.csv_template.tracking_number'),
                    dataIndex: 'trackingNumber',
                    key: 'trackingNumber',
                  },
                  {
                    title: t('shared.orders.upload_labels.csv_template.filename'),
                    dataIndex: 'filename',
                    key: 'filename',
                  },
                ]}
                dataSource={[
                  { key: '1', fulfillmentName: 'ORD-20250328-001', trackingNumber: 'TN123456789', filename: 'ORD-20250328-001.pdf' },
                  { key: '2', fulfillmentName: 'ORD-20250328-002', trackingNumber: 'TN987654321', filename: 'ORD-20250328-002.pdf' },
                ]}
              />
              <div style={{ marginTop: 8, color: '#999', fontSize: '12px' }}>
                {t('shared.orders.upload_labels.step2.format_note')}
              </div>
            </div>

            {/* CSV 欄位規則說明 */}
            <Alert
              type="info"
              message={t('shared.orders.upload_labels.step2.csv_format_rules')}
              description={
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>{t('shared.orders.upload_labels.step2.csv_format_rule_1')}</li>
                  <li>{t('shared.orders.upload_labels.step2.csv_format_rule_2')}</li>
                  <li>{t('shared.orders.upload_labels.step2.csv_format_rule_3')}</li>
                </ul>
              }
            />

            <Upload {...csvUploadProps}>
              <Button icon={<UploadOutlined />}>{t('shared.orders.upload_labels.step2.upload_csv')}</Button>
            </Upload>

            {csvFile && (
              <Alert
                message={t('shared.orders.upload_labels.step2.csv_selected', { filename: csvFile.name })}
                type="success"
                showIcon
              />
            )}

            {!csvFile && pdfFiles.length > 0 && (
              <Alert
                message={t('shared.orders.upload_labels.step2.optional_hint')}
                type="info"
                showIcon
              />
            )}
          </Space>
        </div>
      ),
    },
    {
      title: t('shared.orders.upload_labels.steps.preview'),
      content: (
        <div style={{ padding: '24px 0' }}>
          {/* 整體上傳進度 */}
          {parsedData.some(item => item.uploadStatus) && (() => {
            const total = parsedData.length;
            const uploaded = parsedData.filter(item => item.uploadStatus === UPLOAD_STATUS.SUCCESS).length;
            const failed = parsedData.filter(item => item.uploadStatus === UPLOAD_STATUS.FAILED).length;
            const uploading = parsedData.filter(item => item.uploadStatus === UPLOAD_STATUS.UPLOADING).length;
            const percent = total > 0 ? Math.round(((uploaded + failed) / total) * 100) : 0;

            return (
              <div style={{ marginBottom: 24 }}>
                <div style={{ marginBottom: 8, fontWeight: 500 }}>
                  {t('shared.orders.upload_labels.step3.overall_progress')}
                </div>
                <Progress
                  percent={percent}
                  status={uploading > 0 ? 'active' : (failed > 0 ? 'exception' : 'success')}
                  format={() => `${uploaded + failed}/${total}`}
                />
                <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                  <Space size="large">
                    <span>
                      <CheckCircleOutlined style={{ color: '#52c41a' }} /> {t('shared.orders.upload_labels.step3.success_count', { count: uploaded })}
                    </span>
                    {failed > 0 && (
                      <span>
                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> {t('shared.orders.upload_labels.step3.failed_count', { count: failed })}
                      </span>
                    )}
                    {uploading > 0 && (
                      <span>
                        <LoadingOutlined style={{ color: '#1890ff' }} /> {t('shared.orders.upload_labels.step3.uploading_count', { count: uploading })}
                      </span>
                    )}
                  </Space>
                </div>
              </div>
            );
          })()}

          <Table
            columns={previewColumns}
            dataSource={parsedData}
            rowKey={(record, index) => index}
            pagination={false}
            size="small"
          />

          {parsedData.some(item => item.status === ROW_STATUS.FULFILLMENT_NOT_FOUND) && (
            <Alert
              message={t('shared.orders.upload_labels.step3.order_not_found_warning')}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          {parsedData.some(item => item.status === ROW_STATUS.MISSING) && (
            <Alert
              message={t('shared.orders.upload_labels.step3.missing_files_warning')}
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <Card>
      <Steps current={currentStep} items={steps.map(s => ({ title: s.title }))} style={{ marginBottom: 24 }} />

      <div>{steps[currentStep].content}</div>

      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <Space>
          {currentStep > 0 && (
            <Button onClick={() => setCurrentStep(currentStep - 1)}>
              {t('common.button.previous')}
            </Button>
          )}

          {currentStep === 0 && (
            <Button type="primary" onClick={() => setCurrentStep(1)}>
              {t('common.button.next')}
            </Button>
          )}

          {currentStep === 1 && (
            <Button type="primary" onClick={handleParseCSV}>
              {t('common.button.next')}
            </Button>
          )}

          {currentStep === 2 && (
            <>
              <Button onClick={onCancel}>{t('common.button.cancel')}</Button>
              <Button type="primary" loading={loading} onClick={handleUpload}>
                {t('common.button.confirm_upload')}
              </Button>
            </>
          )}
        </Space>
      </div>
    </Card>
  );
}
